/**
 * Import d'un export **Bitwarden JSON** (R7) : export en clair
 * (`bitwarden_export.json`) ET export protege par mot de passe
 * (`bitwarden_encrypted_export.json`).
 *
 * Purement local : le fichier est dechiffre sur l'appareil, jamais envoye.
 * Le mot de passe de l'export n'est ni conserve ni journalise.
 *
 * INVARIANT CLAUDE.md : aucune crypto maison. L'export protege de Bitwarden
 * utilise PBKDF2-SHA256 ou Argon2id, puis HKDF-SHA256 (`enc` / `mac`), puis
 * AES-256-CBC authentifie par HMAC-SHA-256 (chaine `EncString` de type 2,
 * `2.iv|ciphertext|mac`). Les primitives viennent de `@noble/*`, et le MAC est
 * verifie AVANT le dechiffrement : jamais de texte clair non authentifie.
 *
 * La derivation ne fige pas l'onglet : Argon2id passe par `argon2idAsync` de la
 * meme bibliotheque (rend la main, signale son avancement), PBKDF2 par WebCrypto
 * (natif, hors du fil principal ; 600 000 tours par defaut chez Bitwarden, soit
 * plusieurs secondes d'onglet fige en JavaScript synchrone).
 *
 * Les exports chiffres avec la cle du COMPTE (case « protege par mot de passe »
 * decochee) ne sont pas dechiffrables hors du compte Bitwarden : ils sont
 * refuses avec un message qui dit quoi faire, au lieu d'echouer obscurement.
 */
import { argon2idAsync } from '@noble/hashes/argon2.js';
import { expand } from '@noble/hashes/hkdf.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { cbc } from '@noble/ciphers/aes.js';
import { base64ToBytes, type EntryCustomField } from '@klavox/crypto-core';
import type { UriMatchRule, VaultItemType } from '@klavox/vault-model';
import {
  ImportPasswordInvalidError,
  ImportPasswordRequiredError,
  joinNotes,
  normalizeTotpSecret,
  type ImportedEntry,
} from './csv-import.js';
import type { PasswordHistoryEntry } from './password-history.js';
import { checkboxValue } from './custom-fields.js';
import { normalizeTypedFields, typedFieldGroup } from './typed-fields.js';

/** `type` des elements Bitwarden. */
const BITWARDEN_TYPES: Record<number, VaultItemType> = { 1: 'login', 2: 'note', 3: 'card', 4: 'identity', 5: 'ssh-key' };
/**
 * `UriMatchType` de Bitwarden ; null = « defaut » (celui du coffre), donc absent. 0 (domaine de base) est un choix
 * EXPLICITE : il est garde, pour tenir meme si le defaut du coffre est plus strict.
 */
const BITWARDEN_URI_MATCH: Readonly<Record<number, UriMatchRule>> = { 0: 'domain', 1: 'host', 2: 'starts-with', 3: 'exact', 4: 'regex', 5: 'never' };

/** `FieldType.Boolean` de Bitwarden : case a cocher, valeur `true` / `false`. */
const BITWARDEN_FIELD_BOOLEAN = 2;

/** Champs `identity` de Bitwarden -> champs types Klavox (`typed-fields.ts`). */
const BITWARDEN_IDENTITY_FIELDS: Record<string, string> = {
  title: 'title',
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  company: 'company',
  city: 'city',
  state: 'region',
  postalCode: 'postalCode',
  country: 'country',
  ssn: 'socialSecurityNumber',
  passportNumber: 'passportNumber',
  licenseNumber: 'licenseNumber',
};

/** Plafonds de derivation : un export legitime reste tres en dessous. */
const MAX_PBKDF2_ITERATIONS = 10_000_000;
const MAX_ARGON2_MEMORY_MIB = 1024;

export class BitwardenImportError extends Error {
  override readonly name = 'BitwardenImportError';
  constructor(message: string) {
    super(`export Bitwarden illisible : ${message}`);
  }
}

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type JsonObject = { [key: string]: Json };

function asObject(value: Json | undefined): JsonObject | null {
  return value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asArray(value: Json | undefined): Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json | undefined): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

// --- Export protege par mot de passe ---------------------------------------

/** Chaine `EncString` Bitwarden de type 2 : `2.iv|ciphertext|mac`, tout en base64. */
function decryptEncString(raw: string, encKey: Uint8Array, macKey: Uint8Array): Uint8Array {
  const [type, body] = raw.split('.', 2);
  if (type !== '2' || body === undefined) throw new BitwardenImportError('format de chiffrement non supporte');
  const parts = body.split('|');
  if (parts.length < 3) throw new BitwardenImportError('bloc chiffre incomplet');
  const iv = base64ToBytes(parts[0]!);
  const ciphertext = base64ToBytes(parts[1]!);
  const mac = base64ToBytes(parts[2]!);
  const expected = hmac(sha256, macKey, new Uint8Array([...iv, ...ciphertext]));
  let diff = mac.length === expected.length ? 0 : 1;
  for (let i = 0; i < Math.min(mac.length, expected.length); i++) diff |= mac[i]! ^ expected[i]!;
  // MAC verifie AVANT dechiffrement : un mot de passe faux s'arrete ici.
  if (diff !== 0) throw new ImportPasswordInvalidError('Bitwarden');
  return cbc(encKey, iv).decrypt(ciphertext);
}

/**
 * Derive la cle d'un export protege par mot de passe, selon le KDF declare
 * dans le fichier (0 = PBKDF2-SHA256, 1 = Argon2id, le sel etant alors hache
 * en SHA-256 comme le fait le client Bitwarden). Argon2id dit son avancement
 * (0..1) ; PBKDF2 natif n'en donne pas, seule la fin est signalee.
 */
async function deriveExportKey(
  file: JsonObject,
  password: string,
  onProgress?: (fraction: number) => void,
): Promise<{ encKey: Uint8Array; macKey: Uint8Array }> {
  const salt = new TextEncoder().encode(asString(file['salt']));
  const iterations = Number(file['kdfIterations'] ?? 0);
  if (!Number.isInteger(iterations) || iterations < 1 || iterations > MAX_PBKDF2_ITERATIONS) {
    throw new BitwardenImportError('parametres de derivation hors limites');
  }
  const kdfType = Number(file['kdfType'] ?? 0);
  let key: Uint8Array;
  if (kdfType === 1) {
    const memoryMib = Number(file['kdfMemory'] ?? 64);
    const parallelism = Number(file['kdfParallelism'] ?? 4);
    if (memoryMib < 1 || memoryMib > MAX_ARGON2_MEMORY_MIB || parallelism < 1 || parallelism > 64) {
      throw new BitwardenImportError('parametres Argon2id hors limites');
    }
    key = await argon2idAsync(new TextEncoder().encode(password), sha256(salt), {
      t: iterations,
      m: memoryMib * 1024,
      p: parallelism,
      dkLen: 32,
      asyncTick: 100,
      onProgress,
    });
  } else if (kdfType === 0) {
    const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
    key = new Uint8Array(await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, material, 256));
    onProgress?.(1);
  } else {
    throw new BitwardenImportError(`fonction de derivation ${kdfType} inconnue`);
  }
  return {
    encKey: expand(sha256, key, new TextEncoder().encode('enc'), 32),
    macKey: expand(sha256, key, new TextEncoder().encode('mac'), 32),
  };
}

/**
 * Rend le JSON en clair d'un export protege par mot de passe.
 *
 * @throws {ImportPasswordRequiredError} export chiffre sans mot de passe fourni
 * @throws {ImportPasswordInvalidError} mot de passe refuse (MAC invalide)
 */
async function decryptExport(file: JsonObject, password: string | undefined, onProgress?: (fraction: number) => void): Promise<JsonObject> {
  if (file['passwordProtected'] !== true) {
    throw new BitwardenImportError(
      'export chiffre avec la cle du compte : reexportez en cochant « Protege par mot de passe » (ou en JSON non chiffre)',
    );
  }
  if (password === undefined) throw new ImportPasswordRequiredError('Bitwarden chiffre');
  const { encKey, macKey } = await deriveExportKey(file, password, onProgress);
  const validation = asString(file['encKeyValidation_DO_NOT_EDIT']);
  if (validation.length > 0) {
    const decoded = new TextDecoder().decode(decryptEncString(validation, encKey, macKey));
    if (decoded !== asString(file['salt'])) throw new ImportPasswordInvalidError('Bitwarden');
  }
  const data = asString(file['data']);
  if (data.length === 0) throw new BitwardenImportError('donnees chiffrees absentes');
  const plain = new TextDecoder().decode(decryptEncString(data, encKey, macKey));
  const parsed = asObject(JSON.parse(plain) as Json);
  if (parsed === null) throw new BitwardenImportError('contenu dechiffre invalide');
  return parsed;
}

// --- Elements ---------------------------------------------------------------

function parseBitwardenItem(item: JsonObject, folders: Map<string, readonly string[]>): ImportedEntry {
  const type = BITWARDEN_TYPES[Number(item['type'] ?? 0)] ?? 'note';
  const login = asObject(item['login']) ?? {};
  const extras: string[] = [];
  const typed: Record<string, string> = {};

  if (type === 'card') {
    const card = asObject(item['card']) ?? {};
    typed['cardholder'] = asString(card['cardholderName']);
    typed['number'] = asString(card['number']);
    typed['cvv'] = asString(card['code']);
    const month = asString(card['expMonth']);
    const year = asString(card['expYear']);
    if (month.length > 0 && year.length > 0) typed['expiry'] = `${month.padStart(2, '0')}/${year}`;
    const brand = asString(card['brand']);
    if (brand.length > 0) extras.push(`Reseau : ${brand}`);
  } else if (type === 'identity') {
    const identity = asObject(item['identity']) ?? {};
    for (const [source, key] of Object.entries(BITWARDEN_IDENTITY_FIELDS)) {
      const value = asString(identity[source]);
      if (value.length > 0) typed[key] = value;
    }
    const middle = asString(identity['middleName']);
    if (middle.length > 0) typed['firstName'] = `${typed['firstName'] ?? ''} ${middle}`.trim();
    const address = [identity['address1'], identity['address2'], identity['address3']].map((part) => asString(part as Json)).filter(Boolean);
    if (address.length > 0) typed['address'] = address.join(', ');
    const identityUsername = asString(identity['username']);
    if (identityUsername.length > 0) extras.push(`Identifiant : ${identityUsername}`);
  }

  // Champs personnalises : ranges en champs, un champ « masque » (type 1) le reste, une « case a cocher » (type 2,
  // valeur `true` / `false`) aussi -- decochee comprise. Un champ « lie » (type 3) n'a pas de valeur propre, il pointe
  // vers l'identifiant ou le mot de passe deja importes.
  const customFields: EntryCustomField[] = [];
  for (const raw of asArray(item['fields'])) {
    const field = asObject(raw);
    if (field === null) continue;
    const value = asString(field['value']);
    if (Number(field['type']) === BITWARDEN_FIELD_BOOLEAN) customFields.push({ name: asString(field['name']), value: checkboxValue(value), kind: 'boolean' });
    else if (value.length > 0) customFields.push({ name: asString(field['name']), value, hidden: Number(field['type']) === 1 });
  }

  const totp = normalizeTotpSecret(asString(login['totp']));
  if (totp.extraNote.length > 0) extras.push(totp.extraNote);

  const passwordHistory: PasswordHistoryEntry[] = [];
  for (const raw of asArray(item['passwordHistory'])) {
    const history = asObject(raw);
    const password = asString(history?.['password']);
    if (password.length === 0) continue;
    const changedAt = Date.parse(asString(history?.['lastUsedDate']));
    passwordHistory.push({ password, changedAt: Number.isFinite(changedAt) ? changedAt : 0 });
  }

  // Pose du mot de passe courant : `passwordRevisionDate` (null tant qu'il n'a jamais change), sinon creation de l'element.
  const passwordChangedAt = Date.parse(asString(login['passwordRevisionDate']) || asString(item['creationDate']));

  const uris: string[] = [];
  // Map, pas un objet : une adresse « __proto__ » importee ne touche jamais au prototype.
  const uriMatch = new Map<string, UriMatchRule>();
  for (const raw of asArray(login['uris'])) {
    const uri = asString(asObject(raw)?.['uri']);
    if (uri.length === 0) continue;
    uris.push(uri);
    // `match` : null = defaut du coffre, 0 domaine, 1 hote, 2 commence par, 3 exacte, 4 expression reguliere, 5 jamais.
    const rule = BITWARDEN_URI_MATCH[Number(asObject(raw)?.['match'] ?? -1)];
    if (rule !== undefined && !uriMatch.has(uri)) uriMatch.set(uri, rule);
  }
  const folderPath = folders.get(asString(item['folderId']));
  const group = typedFieldGroup(type);
  // Type 5 (Bitwarden 2025+) : `sshKey.privateKey` / `publicKey` deviennent la paire de l'entree ; l'empreinte se
  // recalcule a l'affichage.
  const sshKey = type === 'ssh-key' ? asObject(item['sshKey']) ?? {} : null;
  return {
    type,
    name: asString(item['name']) || uris[0] || asString(login['username']) || 'Sans nom',
    username: asString(login['username']),
    password: asString(login['password']),
    uris,
    ...(uriMatch.size > 0 ? { uriMatch: Object.fromEntries(uriMatch) } : {}),
    notes: joinNotes([asString(item['notes']), ...extras]),
    totpSecret: totp.secret,
    favorite: item['favorite'] === true,
    // `reprompt: 1` = « Redemander le mot de passe maitre » chez Bitwarden : l'entree reste protegee chez nous.
    ...(Number(item['reprompt']) === 1 ? { reprompt: true } : {}),
    // `archivedDate` (Bitwarden 2025+) : l'entree est aux archives, elle y reste.
    ...(typeof item['archivedDate'] === 'string' && item['archivedDate'].length > 0 ? { archived: true } : {}),
    ...(folderPath !== undefined ? { folderPath } : {}),
    ...(passwordHistory.length > 0 ? { passwordHistory } : {}),
    ...(Number.isFinite(passwordChangedAt) ? { passwordChangedAt } : {}),
    ...(group !== null ? { fields: normalizeTypedFields(group, typed) } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(sshKey !== null ? { sshKey: { privateKey: asString(sshKey['privateKey']), publicKey: asString(sshKey['publicKey']) } } : {}),
  };
}

/** Un export Bitwarden se reconnait a son tableau `items` ou a son drapeau `encrypted`. */
export function isBitwardenExport(data: Json): boolean {
  const file = asObject(data);
  if (file === null) return false;
  return Array.isArray(file['items']) || typeof file['encrypted'] === 'boolean';
}

/**
 * Parse un export Bitwarden JSON, chiffre ou non. Les dossiers Bitwarden sont
 * conserves comme dossiers du coffre ; les elements a la corbeille sont ignores.
 *
 * @throws {ImportPasswordRequiredError} export protege sans mot de passe
 * @throws {ImportPasswordInvalidError} mot de passe refuse
 * @throws {BitwardenImportError} structure ou algorithme non supportes
 */
export async function parseBitwardenExport(data: Json, password?: string): Promise<ImportedEntry[]> {
  return (await readBitwardenExport(data, password)).entries;
}

/**
 * Chemin d'un dossier de l'export JSON : Bitwarden n'imbrique que sur `/` (son `NestingDelimiter`) et garde le nom
 * tel quel a l'import JSON ; un `\` est un caractere du nom (« Clients\Pro » reste un dossier). Seul son import CSV
 * (`processFolder`) change `\` en `/`, ce que fait `folderPathOf`.
 */
function jsonFolderPathOf(raw: string): readonly string[] | undefined {
  const path = raw
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return path.length > 0 ? path : undefined;
}

/**
 * Entrees ET dossiers declares par l'export, vides compris : Bitwarden recree chaque dossier de `folders`, meme sans
 * element, et un dossier prepare a l'avance ne doit pas disparaitre au demenagement.
 */
export async function readBitwardenExport(
  data: Json,
  password?: string,
  onProgress?: (fraction: number) => void,
): Promise<{ entries: ImportedEntry[]; folders: (readonly string[])[] }> {
  let file = asObject(data);
  if (file === null) throw new BitwardenImportError('structure inattendue');
  if (file['encrypted'] === true) file = await decryptExport(file, password, onProgress);
  // Bitwarden imbrique par le nom : « Perso/Banques » est le sous-dossier « Banques » de « Perso ».
  const folders = new Map<string, readonly string[]>();
  for (const raw of asArray(file['folders'])) {
    const folder = asObject(raw);
    const id = asString(folder?.['id']);
    const path = jsonFolderPathOf(asString(folder?.['name']));
    if (id.length > 0 && path !== undefined) folders.set(id, path);
  }
  const items = file['items'];
  if (!Array.isArray(items)) throw new BitwardenImportError('aucun element dans le fichier');
  const entries: ImportedEntry[] = [];
  for (const raw of items) {
    const item = asObject(raw);
    if (item === null || (item['deletedDate'] !== null && item['deletedDate'] !== undefined)) continue;
    entries.push(parseBitwardenItem(item, folders));
  }
  const declared = new Map([...folders.values()].map((path) => [JSON.stringify(path), path]));
  return { entries, folders: [...declared.values()] };
}
